import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSport } from "../contexts/SportContext";
import { X, ChevronRight, ChevronLeft, Shield, Zap, Target, Gift, Crown, Users, ArrowRight, Trophy } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRICKET_STEPS = [
  {
    icon: Trophy,
    title: "Welcome to APL!",
    description: "Welcome to APL Fantasy Cricket! Build your dream team for the T20 season and compete with friends.",
    color: "from-emerald-500 to-cyan-500",
  },
  {
    icon: Shield,
    title: "Season Mode",
    description: "Build a team of 11 players within your budget. Earn points based on real match performances throughout the tournament.",
    color: "from-blue-500 to-indigo-500",
  },
  {
    icon: Zap,
    title: "Match Day Mode",
    description: "Pick a fresh team for each match. Compete daily with a new squad and climb the Match Day leaderboard.",
    color: "from-orange-500 to-red-500",
  },
  {
    icon: Target,
    title: "Predictor",
    description: "Predict match outcomes — winners, top scorers, totals. Earn bonus points for every correct call.",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: ArrowRight,
    title: "Transfer Window",
    description: "Every week, the transfer window opens. Adjust your team based on form and upcoming matchups.",
    color: "from-teal-500 to-emerald-500",
  },
  {
    icon: Gift,
    title: "Daily Reward",
    description: "Come back every day to collect points and build your streak. Longer streaks earn bigger rewards!",
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: Users,
    title: "Mini Leagues",
    description: "Create or join private leagues with friends. Compete for bragging rights in your own group.",
    color: "from-cyan-500 to-blue-500",
  },
  {
    icon: Crown,
    title: "Go Pro",
    description: "Upgrade to Pro for ad-free gaming, more predictions, extra budget, and exclusive features. Weekly and season plans available.",
    color: "from-amber-500 to-yellow-500",
    link: "/subscribe",
    linkText: "View Pro Plans",
  },
];

const FOOTBALL_STEPS = [
  {
    icon: Trophy,
    title: "Welcome to the World Cup!",
    description: "Welcome to APL Fantasy Football — Football World Cup 2026. Build your dream XI and climb the global leaderboard.",
    color: "from-amber-500 to-yellow-500",
  },
  {
    icon: Shield,
    title: "Build Your Squad",
    description: "Pick 11 players — 1 GK, 3–5 DEF, 3–5 MID, 1–3 FWD — inside a 100-credit budget before kickoff each match day.",
    color: "from-blue-500 to-indigo-500",
  },
  {
    icon: Crown,
    title: "Captain & Vice-Captain",
    description: "Your captain scores 2× points and your vice-captain 1.5×. Pick them wisely for every match day.",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: Target,
    title: "Predictor",
    description: "Predict results, exact scores, goalscorers and more. Earn bonus points for every correct call.",
    color: "from-fuchsia-500 to-purple-500",
  },
  {
    icon: Zap,
    title: "Manager of the Day",
    description: "Top the daily leaderboard to be crowned Manager of the Day and bag a +5 bonus.",
    color: "from-orange-500 to-red-500",
  },
  {
    icon: Users,
    title: "Mini Leagues",
    description: "Create or join private leagues and compete with friends through the tournament.",
    color: "from-cyan-500 to-blue-500",
  },
  {
    icon: ArrowRight,
    title: "Go Pro",
    description: "Upgrade to Pro for ad-free play, more predictions and exclusive features.",
    color: "from-amber-500 to-yellow-500",
    link: "/subscribe",
    linkText: "View Pro Plans",
  },
];

export default function GameplayTutorial({ onComplete }) {
  const { user } = useAuth();
  const { sport } = useSport();
  const STEPS = sport === "football" ? FOOTBALL_STEPS : CRICKET_STEPS;
  const [step, setStep] = useState(0);

  const handleComplete = async () => {
    if (user) {
      try {
        const token = await user.getIdToken();
        await axios.put(`${API}/users/tutorial-seen`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.error("Failed to mark tutorial seen:", e);
      }
    }
    onComplete();
  };

  const handleSkip = () => handleComplete();
  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else handleComplete();
  };
  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="gameplay-tutorial">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Content */}
      <div className="relative z-10 max-w-md w-full mx-4">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          data-testid="tutorial-skip-btn"
          className="absolute -top-12 right-0 text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
        >
          Skip <X className="w-4 h-4" />
        </button>

        {/* Card */}
        <div className="bg-slate-800 border border-slate-700/50 rounded-2xl overflow-hidden">
          {/* Icon header */}
          <div className={`bg-gradient-to-br ${current.color} p-8 flex items-center justify-center`}>
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Text */}
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-2">{current.title}</h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">{current.description}</p>
            {current.link && (
              <a
                href={current.link}
                className="text-amber-400 hover:text-amber-300 text-sm font-medium inline-flex items-center gap-1"
                data-testid="tutorial-subscribe-link"
              >
                {current.linkText} <ChevronRight className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 pb-4">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === step ? "bg-emerald-400 w-6" : i < step ? "bg-emerald-400/50" : "bg-slate-600"
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3 p-4 pt-0">
            {step > 0 && (
              <button
                onClick={handlePrev}
                data-testid="tutorial-prev-btn"
                className="flex-1 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 transition-all flex items-center justify-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              data-testid="tutorial-next-btn"
              className={`flex-1 py-2.5 rounded-xl bg-gradient-to-r ${current.color} text-white text-sm font-bold hover:opacity-90 transition-all flex items-center justify-center gap-1`}
            >
              {step === STEPS.length - 1 ? "Let's Go!" : "Next"} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Step counter */}
        <p className="text-center text-slate-500 text-xs mt-3">
          {step + 1} of {STEPS.length}
        </p>
      </div>
    </div>
  );
}
